import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { notification } from "antd";

import { RootState } from "@/redux/store";
import { clearDownload, setDownloadState } from "@/redux/downloads/reducer";
import { NotificationDescription } from "@/components_v2/Notification/NotificationDescription";
import { downloadPollingService } from "@/shared/services/downloadPollingService";
import { UseUserExportDownloadOptions } from "@/features/users/settings/types";
import {
  useExportUserDataMutation,
  useLazyGetExportedUserDataQuery,
} from "@/redux/service";

export const useUserExportDownload = (
  options: UseUserExportDownloadOptions = {},
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [getExportedUserData] = useLazyGetExportedUserDataQuery();
  const [exportUserData] = useExportUserDataMutation();

  const downloadingStates = useSelector(
    (state: RootState) => state.downloads.downloadingStates,
  );

  const { pollInterval = 2000, timeout = 300000 } = options;

  const showSuccessNotification = useCallback(
    (link: string) => {
      notification.success({
        message: (
          <div>
            <p>
              {t("users.labels.exportReady")}
              {t("users.labels.downloadDoesntStartAutomatically")},{" "}
              <a href={link} target="_blank" download>
                {t("users.labels.clickHere")}
              </a>{" "}
              {t("users.labels.toDownload")}
            </p>
          </div>
        ),
        placement: "bottomRight",
        duration: 30,
      });
    },
    [t],
  );

  const showErrorNotification = useCallback(() => {
    notification.error({
      message: t("users.labels.exportTimeout"),
      placement: "bottomRight",
    });
  }, [t]);

  const showTimeoutNotification = useCallback(() => {
    notification.warning({
      message: t("users.labels.exportTimeout"),
      placement: "bottomRight",
    });
  }, [t]);

  const handleCleanup = useCallback(
    (userId: string) => {
      dispatch(clearDownload(userId));
    },
    [dispatch],
  );

  const exportUser = useCallback(
    async (userId: number, userName?: string) => {
      if (downloadingStates[userId]) {
        return;
      }

      dispatch(
        setDownloadState({
          id: userId.toString(),
          isDownloading: true,
        }),
      );

      const createNotificationDescription = (filename: string) => (
        <NotificationDescription
          filename={filename}
          descriptionKey="users.labels.generatingExportDescription"
        />
      );

      const exportFilename = userName
        ? `${userName} Export.csv`
        : "User Export.csv";

      await exportUserData(userId).unwrap();

      await downloadPollingService.initiateDownload(
        userId.toString(),
        getExportedUserData,
        { pollInterval, timeout },
        {
          onSuccess: (response) => {
            dispatch(
              setDownloadState({
                id: userId.toString(),
                isDownloading: false,
              }),
            );
            showSuccessNotification(response.link);
          },
          onError: () => {
            dispatch(
              setDownloadState({
                id: userId.toString(),
                isDownloading: false,
              }),
            );
            showErrorNotification();
          },
          onTimeout: () => {
            showTimeoutNotification();
          },
          onCleanup: handleCleanup,
        },
        {
          message: t("users.labels.generatingExportTitle"),
          description: createNotificationDescription(exportFilename),
        },
      );
    },
    [
      downloadingStates,
      getExportedUserData,
      dispatch,
      t,
      pollInterval,
      timeout,
      showSuccessNotification,
      showErrorNotification,
      showTimeoutNotification,
      handleCleanup,
    ],
  );

  const isExporting = useCallback(
    (userId: number) => downloadingStates[userId.toString()] || false,
    [downloadingStates],
  );

  return {
    startDownload: exportUser,
    isDownloading: isExporting,
  };
};
